/**
 * Tracked-value and projection mutations — the symmetric self-restoring flips (`hp`, `heat`,
 * `name`, `concealed`, `disclosure`, and the encounter-level live-HP policy), each inverting to
 * the SAME command type carrying the prior value — plus the GM setters for structure, stress,
 * burn, one affordance-ledger entry and a Loading weapon's loaded flag, which invert to a
 * `restore_combatant_base` snapshot.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import { encounterShowEnemyHp, isDisclosureLevel, withDisclosureLevel, type AffordanceStateEntry, type Combatant, type EncounterState } from "../types";
import { findAffordance } from "../model/affordance";
import { loadedResourceKey, weaponInstanceKey } from "../model/weapons";
import { changed, clamp, requireCombatant, withCombatant, type EncounterHandler } from "./context";

export const applySetHp: EncounterHandler<"set_hp"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  if (!Number.isFinite(cmd.hp)) {
    return err(new CommandError(`set_hp: hp ${cmd.hp} is not a finite number`));
  }
  const prev = c.current.hp;
  const hp = clamp(Math.trunc(cmd.hp), 0, c.base.maxHp);
  const next = withCombatant(state, { ...c, current: { ...c.current, hp } });
  return ok({
    state: next,
    inverse: { type: "set_hp", actorId: cmd.actorId, combatantId: c.id, hp: prev },
    events: changed(c.id),
  });
};

export const applySetHeat: EncounterHandler<"set_heat"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  if (c.base.maxHeat === undefined) {
    return err(
      new CommandError(`set_heat: combatant ${c.id} (kind ${c.kind}) has no heat track`),
    );
  }
  if (!Number.isFinite(cmd.heat)) {
    return err(new CommandError(`set_heat: heat ${cmd.heat} is not a finite number`));
  }
  const prev = c.current.heat ?? 0;
  const heat = clamp(Math.trunc(cmd.heat), 0, c.base.maxHeat);
  const next = withCombatant(state, { ...c, current: { ...c.current, heat } });
  return ok({
    state: next,
    inverse: { type: "set_heat", actorId: cmd.actorId, combatantId: c.id, heat: prev },
    events: changed(c.id),
  });
};

/**
 * The GM setters' shared inverse: a byte-exact snapshot of the prior `base` + `current`, so an
 * undo restores an absent field as absent (not as the value a reader would default it to).
 */
function restoreSnapshot(actorId: string, c: Combatant) {
  return { type: "restore_combatant_base" as const, actorId, combatantId: c.id, base: c.base, current: c.current };
}

/** Set one numeric track on `current`, clamped to `[0, max]`; rejected when the unit has no such track. */
function setTrack(args: {
  state: EncounterState;
  cmd: { actorId: string; combatantId: string };
  verb: string;
  field: "structure" | "stress";
  value: number;
}) {
  const { state, cmd, verb, field, value } = args;
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const max = field === "structure" ? c.base.maxStructure : c.base.maxStress;
  if (max === undefined) {
    return err(new CommandError(`${verb}: combatant ${c.id} has no ${field} track`));
  }
  if (!Number.isFinite(value)) {
    return err(new CommandError(`${verb}: ${field} ${value} is not a finite number`));
  }
  const next = withCombatant(state, { ...c, current: { ...c.current, [field]: clamp(Math.trunc(value), 0, max) } });
  return ok({ state: next, inverse: restoreSnapshot(cmd.actorId, c), events: changed(c.id) });
}

export const applySetStructure: EncounterHandler<"set_structure"> = (state, cmd) =>
  setTrack({ state, cmd, verb: "set_structure", field: "structure", value: cmd.structure });

export const applySetStress: EncounterHandler<"set_stress"> = (state, cmd) =>
  setTrack({ state, cmd, verb: "set_stress", field: "stress", value: cmd.stress });

export const applySetBurn: EncounterHandler<"set_burn"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  if (!Number.isFinite(cmd.burn)) {
    return err(new CommandError(`set_burn: burn ${cmd.burn} is not a finite number`));
  }
  const next = withCombatant(state, { ...c, current: { ...c.current, burn: Math.max(0, Math.trunc(cmd.burn)) } });
  return ok({ state: next, inverse: restoreSnapshot(cmd.actorId, c), events: changed(c.id) });
};

export const applySetAffordanceState: EncounterHandler<"set_affordance_state"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  if (cmd.key.length === 0) return err(new CommandError("set_affordance_state: empty key"));
  const uses = cmd.entry?.uses;
  if (uses !== undefined && (!Number.isInteger(uses) || uses < 0)) {
    return err(new CommandError(`set_affordance_state: uses ${uses} is not a non-negative integer`));
  }
  const discharged = cmd.entry?.discharged === true;
  const table = { ...(c.current.affordanceState ?? {}) };
  const put = (key: string, entry: AffordanceStateEntry): void => {
    if (Object.keys(entry).length === 0) delete table[key];
    else table[key] = entry;
  };
  // A pooled Limited affordance counts its uses on the shared pool slot (`limited.pool ?? key`, what
  // `spend_action_use` writes and every pool reader reads); its discharge stays on its own key.
  const pool = findAffordance(c.base.affordances, cmd.key)?.limited?.pool;
  if (pool !== undefined && pool !== cmd.key) {
    const poolEntry: AffordanceStateEntry = { ...table[pool] };
    if (uses !== undefined) poolEntry.uses = uses;
    else delete poolEntry.uses;
    put(pool, poolEntry);
    put(cmd.key, discharged ? { discharged: true } : {});
  } else {
    put(cmd.key, { ...(uses !== undefined ? { uses } : {}), ...(discharged ? { discharged: true as const } : {}) });
  }
  const current = { ...c.current };
  if (Object.keys(table).length === 0) delete current.affordanceState;
  else current.affordanceState = table;
  const next = withCombatant(state, { ...c, current });
  return ok({ state: next, inverse: restoreSnapshot(cmd.actorId, c), events: changed(c.id) });
};

export const applySetWeaponLoaded: EncounterHandler<"set_weapon_loaded"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const weapon = c.base.weapons?.find((w) => weaponInstanceKey(w) === cmd.weapon);
  if (weapon === undefined) return err(new CommandError(`set_weapon_loaded: ${c.name} has no weapon "${cmd.weapon}"`));
  if (weapon.loading !== true) return err(new CommandError(`set_weapon_loaded: ${weapon.name} is not a Loading weapon`));
  const resources = { ...(c.current.resources ?? {}), [loadedResourceKey(cmd.weapon)]: cmd.loaded ? 1 : 0 };
  const next = withCombatant(state, { ...c, current: { ...c.current, resources } });
  return ok({ state: next, inverse: restoreSnapshot(cmd.actorId, c), events: changed(c.id) });
};

export const applySetCombatantName: EncounterHandler<"set_combatant_name"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const prev = c.name;
  const next = withCombatant(state, { ...c, name: cmd.name });
  return ok({
    state: next,
    inverse: {
      type: "set_combatant_name",
      actorId: cmd.actorId,
      combatantId: c.id,
      name: prev,
    },
    events: changed(c.id),
  });
};

export const applySetConcealed: EncounterHandler<"set_concealed"> = (state, cmd) => {
  // GM concealment — a symmetric boolean flip, exactly
  // like `set_combatant_name`: the inverse is the SAME command type carrying the
  // prior value, no separate `restore_*` needed. GM-only at the permission gate
  // (`canIssueEncounter`); this is a broadcast-projection fact, not a rules status.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const prev = c.concealed ?? false;
  const next = withCombatant(state, { ...c, concealed: cmd.concealed });
  return ok({
    state: next,
    inverse: {
      type: "set_concealed",
      actorId: cmd.actorId,
      combatantId: c.id,
      concealed: prev,
    },
    events: changed(c.id),
  });
};

export const applySetDisclosure: EncounterHandler<"set_disclosure"> = (state, cmd) => {
  // GM manual disclosure control — a symmetric self-restoring flip exactly like
  // `set_concealed`, generalized from a boolean to a 3-state-plus-unset enum.
  // GM-only at the permission gate (`canIssueEncounter`); a projection fact, not
  // a rules status. Enum-guard an explicit level (an omitted `disclosure` is the
  // legitimate "clear to unset" case, never rejected).
  if (cmd.disclosure !== undefined && !isDisclosureLevel(cmd.disclosure)) {
    return err(new CommandError(`set_disclosure: unknown disclosure level ${JSON.stringify(cmd.disclosure)}`));
  }
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const prev = c.disclosure;
  const next = withCombatant(state, withDisclosureLevel(c, cmd.disclosure));
  return ok({
    state: next,
    inverse: {
      type: "set_disclosure",
      actorId: cmd.actorId,
      combatantId: c.id,
      disclosure: prev,
    },
    events: changed(c.id),
  });
};

export const applySetTokenImage: EncounterHandler<"set_token_image"> = (state, cmd) => {
  // GM-set token image — a symmetric self-restoring write, exactly like `set_disclosure`: the
  // inverse is the SAME command carrying the prior value, omitted when there was none. GM-only at
  // the permission gate (`canIssueEncounter`); a display fact, not a rule.
  if (cmd.image !== undefined && typeof cmd.image !== "string") {
    return err(new CommandError(`set_token_image: image must be a string, got ${JSON.stringify(cmd.image)}`));
  }
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const prev = c.tokenImage;
  const { tokenImage: _drop, ...rest } = c;
  const nextC: Combatant = cmd.image ? { ...rest, tokenImage: cmd.image } : rest;
  return ok({
    state: withCombatant(state, nextC),
    inverse: {
      type: "set_token_image",
      actorId: cmd.actorId,
      combatantId: c.id,
      ...(prev !== undefined ? { image: prev } : {}),
    },
    events: changed(c.id),
  });
};

export const applySetHpDisclosure: EncounterHandler<"set_hp_disclosure"> = (state, cmd) => {
  // GM per-encounter live-HP policy — a symmetric self-restoring boolean flip, exactly
  // like `set_concealed` but encounter-level (no combatantId). GM-only at the
  // permission gate.
  const prev = encounterShowEnemyHp(state);
  const next: EncounterState = { ...state, showEnemyHp: cmd.showEnemyHp };
  return ok({
    state: next,
    inverse: {
      type: "set_hp_disclosure",
      actorId: cmd.actorId,
      showEnemyHp: prev,
    },
    events: changed(null),
  });
};
